import type { Item } from '@/types/item'
import { formatItemAcquisitionSource } from '@/utils/labels'

export interface ItemAcquisitionPrice {
  itemId: string
  name: string
  amount: number
}

export interface ItemAcquisitionInfo {
  source: string
  label: string
  prices: ItemAcquisitionPrice[]
}

const SOURCE_CATEGORY_LABELS: Record<string, string> = {
  fishing: 'Fishing',
  factory: 'Factory',
  dragon_rings: 'Dragon Rings',
  general: 'General',
}

const PRICE_ITEM_ICON_PATHS: Record<string, string> = {
  drakeblood_extract: 'icons/items/material/drakeblood_extract.png',
  factory_catalyst: 'icons/items/material/factory_catalyst.png',
  fishingRodPart: 'icons/items/material/fishingRodPart.png',
  gold: 'icons/ui/GoldCoin.png',
  old_boot: 'icons/items/material/old_boot.png',
  scaleons: 'icons/items/material/scaleons.png',
  silver: 'icons/items/material/silver.png',
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function readNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function getItemAcquisitionInfo(item: Item): ItemAcquisitionInfo | null {
  const metadata = item.metadata
  if (!isRecord(metadata) || !isRecord(metadata.acquisition)) return null

  const acquisition = metadata.acquisition
  const prices = Array.isArray(acquisition.prices)
    ? acquisition.prices.flatMap((price): ItemAcquisitionPrice[] => {
      if (!isRecord(price)) return []

      const amount = readNumber(price.amount)
      if (amount === null) return []

      return [
        {
          itemId: readString(price.itemId),
          name: readString(price.name),
          amount,
        },
      ]
    })
    : []

  return {
    source: readString(acquisition.source),
    label: readString(acquisition.label),
    prices,
  }
}

export function getItemSourceLabel(item: Item) {
  const acquisition = getItemAcquisitionInfo(item)
  if (acquisition?.label) {
    if (item.subType === 'fish' && acquisition.label.toLowerCase() === 'fishing catch') {
      return ''
    }
    return acquisition.label
  }

  if (item.type === 'consumable' || item.type === 'fishing') return ''
  return formatItemAcquisitionSource(item.acquisitionSource)
}

export function getItemSourceCategory(item: Item) {
  const metadata = item.metadata
  if (isRecord(metadata)) {
    const category = readString(metadata.sourceCategory)
    if (category) return category
  }
  if (item.type === 'fishing') return 'fishing'
  if (item.type === 'consumable') return 'general'
  return ''
}

export function getItemSourceCategoryLabel(item: Item) {
  const metadata = item.metadata
  if (isRecord(metadata)) {
    const label = readString(metadata.sourceCategoryLabel)
    if (label) return label
  }
  const category = getItemSourceCategory(item)
  return SOURCE_CATEGORY_LABELS[category] ?? category
}

export function formatItemSourceCategory(value: string) {
  return SOURCE_CATEGORY_LABELS[value] ?? value
}

export function getAcquisitionPriceIconPath(price: ItemAcquisitionPrice) {
  return PRICE_ITEM_ICON_PATHS[price.itemId] ?? ''
}
